import { Maximize2, PanelRightOpen, X } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useLayoutEffect, useRef } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { embeddedBrowserAvailabilityStore } from "../../browser/availability"
import { embeddedBrowserStore } from "../../browser/store"
import { embeddedBrowserSurfaceStore } from "../../browser/surfaceStore"
import { Z_INDEX } from "../../constants"
import { PortalContainerProvider } from "../../hooks/usePortalContainer"
import { useMediaQuery } from "../../hooks/useMediaQuery"
import { useResolvedTheme } from "../../hooks/useResolvedTheme"
import { useCodeStore } from "../../store/context"
import { dockedPaneWidthStore } from "../../store/managers/dockedPaneWidthStore"
import { requestWildLarkMethod } from "../../wildlark/client"
import { codeSidebarManager } from "../sidebar/sidebarManager"
import { DockedPaneResizable } from "../tray/DockedPaneResizable"
import { CodeModalProvider } from "../ui/CodeModalProvider"
import { BrowserPanel } from "./BrowserPanel"

const surfaceButton =
    "btn flex h-7 w-7 shrink-0 items-center justify-center border border-transparent text-muted transition-colors hover:border-border hover:bg-base-100 hover:text-base-content"
const dockedSurfaceButton =
    "btn flex h-8 shrink-0 items-center justify-center gap-1.5 border border-border bg-base-100 px-2 text-xs font-medium text-muted transition-colors hover:text-base-content"
const focusableSelector = [
    "button:not([disabled])",
    "a[href]",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[contenteditable='true']",
    "[contenteditable='plaintext-only']",
    "[tabindex]:not([tabindex='-1'])",
].join(",")

/**
 * Stable Browser host mounted above route content. Browser tabs and webviews
 * therefore survive task/workspace navigation while their dock still reserves
 * space in each route's CodeAppLayout.
 */
export const GlobalBrowserSurface = observer(function GlobalBrowserSurface(): JSX.Element | null {
    const dialogRef = useRef<HTMLElement>(null)
    const codeStore = useCodeStore()
    const compact = useMediaQuery("(max-width: 719px)")
    const themeSetting = codeStore.personalSettingsStore?.settings.current.theme ?? "system"
    const themeClass = useResolvedTheme(themeSetting)
    const available = embeddedBrowserAvailabilityStore.available
    const running = available && embeddedBrowserSurfaceStore.isRunning
    const visible = running && embeddedBrowserSurfaceStore.isVisible
    const docked = visible && embeddedBrowserSurfaceStore.isDocked && !compact
    const modal = visible && !docked

    useEffect(
        () =>
            embeddedBrowserStore.setUserActionOutcomeHandler(async (request, outcome, failureReason) => {
                const taskId = codeStore.findTaskIdForNestSession(request.sessionId)
                const taskModel = taskId ? codeStore.tasks.getTaskModel(taskId) : null
                if (!taskId || !taskModel?.repoId) throw new Error("The originating Browser task is no longer available.")
                await requestWildLarkMethod("wildlark.task.session.send", {
                    taskId,
                    workspaceId: taskModel.repoId,
                    sessionId: request.sessionId,
                    targetSessionId: request.sessionId,
                    browserActionOutcome: {
                        requestId: request.id,
                        instruction: request.instruction,
                        status: outcome,
                        ...(failureReason ? { reason: failureReason } : {}),
                        ...(request.userNotes ? { userNotes: request.userNotes } : {}),
                    },
                    permissionMode: request.permissionMode,
                    clientRequestId: `${taskId}:browser-user-action:${request.id}`,
                })
            }),
        [codeStore]
    )

    useEffect(() => {
        codeSidebarManager.setGlobalBrowserDocked(docked)
        return () => codeSidebarManager.setGlobalBrowserDocked(false)
    }, [docked])

    useLayoutEffect(() => {
        embeddedBrowserStore.setSurfaceInteractive(visible)
        return () => embeddedBrowserStore.setSurfaceInteractive(false)
    }, [visible])

    useEffect(() => {
        if (!modal) return
        const background = document.querySelector<HTMLElement>("[data-code-app-shell='true']")
        const backgroundWasInert = background?.inert ?? false
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
        if (background) background.inert = true

        const focusInside = (last = false) => {
            const dialog = dialogRef.current
            if (!dialog) return
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
            const target = last ? focusable[focusable.length - 1] : focusable[0]
            ;(target ?? dialog).focus()
        }
        focusInside()

        const handleTabKey = (event: KeyboardEvent) => {
            if (event.key !== "Tab") return
            const dialog = dialogRef.current
            if (!dialog) return
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (!first || !last) {
                event.preventDefault()
                dialog.focus()
            } else if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
                event.preventDefault()
                first.focus()
            }
        }
        const containFocus = (event: FocusEvent) => {
            const dialog = dialogRef.current
            if (dialog && event.target instanceof Node && !dialog.contains(event.target)) focusInside()
        }
        document.addEventListener("keydown", handleTabKey)
        document.addEventListener("focusin", containFocus)
        return () => {
            document.removeEventListener("keydown", handleTabKey)
            document.removeEventListener("focusin", containFocus)
            if (background) background.inert = backgroundWasInert
            if (previouslyFocused?.isConnected) previouslyFocused.focus()
        }
    }, [modal])

    useHotkeys("escape", () => embeddedBrowserSurfaceStore.hide(), {
        enabled: visible,
        enableOnContentEditable: true,
        enableOnFormTags: true,
    })

    if (!running) return null

    const presentation = visible ? (docked ? "docked" : "modal") : "hidden"
    const paneClass = docked
        ? `absolute bottom-0 right-0 top-0 flex min-h-0 flex-col border-l border-border bg-base-100 text-base-content shadow-[-12px_0_36px_rgba(0,0,0,0.12)] ease-in-out motion-reduce:transition-none ${
              dockedPaneWidthStore.isResizing ? "transition-none" : "transition-[width] duration-300"
          }`
        : "absolute bottom-[clamp(1rem,3vh,2rem)] left-[clamp(1rem,2vw,2rem)] right-[clamp(1rem,2vw,2rem)] top-[clamp(1rem,3vh,2rem)] flex min-h-0 flex-col overflow-hidden border border-border bg-base-100 text-base-content shadow-[0_28px_90px_rgba(0,0,0,0.38),0_8px_28px_rgba(0,0,0,0.2)]"
    const hiddenInteractionProps = visible ? {} : { inert: "", "aria-hidden": true }

    return (
        <div className={`code-theme ${themeClass}`} data-global-browser-host="true">
            <CodeModalProvider>
                <PortalContainerProvider portalId="global-browser-portal-root">
                    <div
                        className="pointer-events-none fixed inset-0"
                        style={{
                            zIndex: docked ? Z_INDEX.GLOBAL_BROWSER_DOCK : Z_INDEX.TASK_TRAY_ROOT,
                        }}
                        data-global-browser-surface="true"
                        data-presentation={presentation}
                    >
                        {modal && (
                            <div
                                className="pointer-events-auto absolute inset-0 bg-black/25 backdrop-blur-[3px]"
                                aria-hidden="true"
                                onClick={() => embeddedBrowserSurfaceStore.hide()}
                                data-global-browser-backdrop="true"
                            />
                        )}
                        <section
                            ref={dialogRef}
                            className={`${paneClass} ${visible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
                            style={docked ? { width: dockedPaneWidthStore.cssWidth } : undefined}
                            role={visible ? "dialog" : undefined}
                            aria-modal={modal || undefined}
                            aria-label="Browser"
                            tabIndex={visible ? -1 : undefined}
                            {...hiddenInteractionProps}
                        >
                            <DockedPaneResizable enabled={docked}>
                                <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border bg-base-200/90 px-3">
                                    <h2 className="min-w-0 flex-1 truncate text-xs font-semibold">Browser</h2>
                                    {docked ? (
                                        <button
                                            type="button"
                                            className={dockedSurfaceButton}
                                            onClick={() => embeddedBrowserSurfaceStore.showModal()}
                                            aria-label="Open Browser as a modal"
                                            title="Open as modal"
                                        >
                                            <Maximize2 size={14} />
                                            <span>Modal</span>
                                        </button>
                                    ) : (
                                        !compact &&
                                        visible && (
                                            <button
                                                type="button"
                                                className={surfaceButton}
                                                onClick={() => embeddedBrowserSurfaceStore.dock()}
                                                aria-label="Dock Browser to the right"
                                                title="Dock to the right"
                                            >
                                                <PanelRightOpen size={15} />
                                            </button>
                                        )
                                    )}
                                    <button
                                        type="button"
                                        className={docked ? `${dockedSurfaceButton} hover:border-error/50 hover:text-error` : surfaceButton}
                                        aria-label="Hide Browser"
                                        title="Hide Browser"
                                        onClick={() => embeddedBrowserSurfaceStore.hide()}
                                    >
                                        <X size={15} />
                                        {docked && <span>Hide</span>}
                                    </button>
                                </header>
                                <div className="min-h-0 flex-1 overflow-hidden bg-base-100">
                                    <BrowserPanel />
                                </div>
                            </DockedPaneResizable>
                        </section>
                    </div>
                </PortalContainerProvider>
            </CodeModalProvider>
        </div>
    )
})
